import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Col, Typography, Spin, Empty, Button, Card, Tag } from 'antd';
import { CustomerServiceOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useTheme } from '../../theme/ThemeProvider';
import { httpService } from '../../services/httpService';
import { Trip } from '../../types/trip';
import TripChatPopup from '../../components/TripChatPopup';
import { getTripTypePersian, getTripStateText } from '../../utils/tripUtils';
import './styles.css';

const { Title, Text } = Typography;

const TripsHistory: React.FC = () => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [openChatTrips, setOpenChatTrips] = useState<Set<string>>(new Set());

  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [pageSize] = useState(10);
  const { theme } = useTheme();
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    fetchTrips();
  }, []);

  const fetchTrips = async (page: number = 1, append: boolean = false) => {
    console.log(`🚀 Fetching trips - Page: ${page}, Append: ${append}, Timestamp: ${new Date().toISOString()}`);
    
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      
      const response = await httpService.getTripList(page, pageSize);
      console.log(`✅ Trips fetched successfully - Page: ${page}, Count: ${response.items?.length || 0}`);
      
      const newTrips = response.items || [];
      
      if (append) {
        setTrips(prev => [...prev, ...newTrips]);
      } else {
        setTrips(newTrips);
      }
      
      setTotalCount(response.totalCount || 0);
      setHasMore(newTrips.length === pageSize);
    } catch (error) {
      console.error('❌ Error fetching trips:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    // Add a small delay to prevent rapid successive calls
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Calculate next page based on current trips length
    const nextPage = Math.floor(trips.length / pageSize) + 1;
    await fetchTrips(nextPage, true);
  }, [loadingMore, hasMore, fetchTrips, trips.length, pageSize]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const currentLoadingRef = loadingRef.current;
    
    if (currentLoadingRef && hasMore && !loadingMore) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasMore && !loadingMore) {
            loadMore();
          }
        },
        { 
          threshold: 0.1,
          rootMargin: '100px' // Start loading before reaching the bottom
        }
      );

      observerRef.current.observe(currentLoadingRef);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loadMore]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Chat management functions
  const openTripChat = (tripId: string) => {
    setOpenChatTrips(prev => new Set(prev).add(tripId));
  };

  const closeTripChat = (tripId: string) => {
    setOpenChatTrips(prev => {
      const newSet = new Set(prev);
      newSet.delete(tripId);
      return newSet;
    });
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const persianDate = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
        year: 'numeric',
        weekday: 'long',
        day: 'numeric',
        month: 'long'
      }).formatToParts(date);
      
      const year = persianDate.find(part => part.type === 'year')?.value || '';
      const dayOfWeek = persianDate.find(part => part.type === 'weekday')?.value || '';
      const dayOfMonth = persianDate.find(part => part.type === 'day')?.value || '';
      const month = persianDate.find(part => part.type === 'month')?.value || '';
      
      return `${year} ${dayOfWeek} ${dayOfMonth} ${month}`;
    } catch (error) {
      console.error('❌ Error formatting Persian date:', error);
      return 'تاریخ نامعتبر';
    }
  };

  const formatTime = (timeString: string) => {
    return timeString.substring(0, 5);
  };

  if (loading) {
    return (
      <div className="trips-history-container">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>در حال بارگذاری سفرها...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="trips-history-container">
        <div className="trips-history-header">
         <div style={{ display: 'inline-flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <div>
             <Title level={2} style={{ color: theme.text.primary, margin: 0, lineHeight: 1.2, display: 'inline-block' }}>
              سفرهای من 
            </Title>
            <Text type="secondary" style={{ fontSize: '16px' }}>
               ({totalCount})
            </Text>
          </div>
          {/* removed top-level support chat button */}
        </div>
      </div>

      {trips.length === 0 ? (
        <Empty
          description="هیچ سفری یافت نشد"
          style={{ margin: '50px 0' }}
        />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {trips.map((trip) => {
              const stateInfo = getTripStateText(trip.state);
              return (
                <Col xs={24} sm={12} lg={8} xl={6} key={trip.id}>
                  <Card 
                    className="trip-card"
                    hoverable
                    style={{ 
                      backgroundColor: theme.surface,
                      borderColor: theme.border,
                      color: theme.text.primary,
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    {/* Date, Time and Trip Type */}
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginBottom: '16px',
                      paddingBottom: '12px',
                      borderBottom: `1px solid ${theme.border}`
                    }}>
                      <Text style={{ 
                        color: theme.text.primary, 
                        fontSize: '14px'
                      }}>
                        {formatDate(trip.startDate)} - {formatTime(trip.startTime)}
                      </Text>
                      <Tag color="blue" style={{ fontSize: '12px' }}>
                        {getTripTypePersian(trip.type)}
                      </Tag>
                    </div>

                    {/* Trip Status */}
                    <div style={{ marginBottom: '16px' }}>
                      <Text style={{ 
                        color: theme.text.secondary, 
                        fontSize: '14px'
                      }}>
                        وضعیت سفر: {stateInfo.text}
                      </Text>
                    </div>

                    {/* Source and Destination */}
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <EnvironmentOutlined style={{ color: theme.primary, fontSize: '16px', marginTop: '2px' }} />
                          <Text strong style={{ color: theme.text.primary, marginRight: '8px', whiteSpace: 'nowrap' }}>
                            مبدا:
                          </Text>
                          <Text style={{ 
                            color: theme.text.secondary, 
                            fontSize: '14px',
                            textAlign: 'right',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                            flex: 1,
                            minWidth: 0
                          }}>
                            {trip.source.address}
                          </Text>
                        </div>
                      </div>

                      <div>
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                          <EnvironmentOutlined style={{ color: theme.success, fontSize: '16px', marginTop: '2px' }} />
                          <Text strong style={{ color: theme.text.primary, marginRight: '8px', whiteSpace: 'nowrap' }}>
                            مقصد:
                          </Text>
                          <Text style={{ 
                            color: theme.text.secondary, 
                            fontSize: '14px',
                            textAlign: 'right',
                            wordWrap: 'break-word',
                            whiteSpace: 'pre-wrap',
                            flex: 1,
                            minWidth: 0
                          }}>
                            {trip.destination.address}
                          </Text>
                        </div>
                      </div>
                    </div>

                    {/* Chat Button */}
                    <div style={{ 
                      marginTop: '16px', 
                      display: 'flex', 
                      justifyContent: 'center',
                      borderTop: `1px solid ${theme.border}`,
                      paddingTop: '12px'
                    }}>
                      <Button
                        type="primary"
                        icon={<CustomerServiceOutlined />}
                        onClick={() => openTripChat(trip.id)}
                        size="small"
                        style={{
                          backgroundColor: theme.primary,
                          borderColor: theme.primary,
                          borderRadius: '6px',
                          fontSize: '12px',
                          height: '32px',
                          paddingLeft: '12px',
                          paddingRight: '12px'
                        }}
                      >
                        چت پشتیبانی
                      </Button>
                    </div>
                  </Card>

                  {/* Individual Trip Chat Popup */}
                  <TripChatPopup
                    isOpen={openChatTrips.has(trip.id)}
                    onClose={() => closeTripChat(trip.id)}
                    tripId={trip.id}
                    tripTitle={`سفر ${formatDate(trip.startDate)} - ${trip.source.address} به ${trip.destination.address}`}
                  />
                </Col>
              );
            })}
          </Row>

          {/* Infinite Scroll Loading Indicator */}
          {hasMore && (
            <div 
              ref={loadingRef}
              className="infinite-scroll-loading"
              style={{ 
                textAlign: 'center', 
                padding: '20px',
                marginTop: '20px'
              }}
            >
              {loadingMore ? (
                <div>
                  <Spin size="large" />
                  <div style={{ marginTop: '16px' }}>در حال بارگذاری سفرهای بیشتر...</div>
                </div>
              ) : (
                <div className="infinite-scroll-hint">
                  اسکرول کنید تا سفرهای بیشتری ببینید
                </div>
              )}
            </div>
          )}

          {/* End of trips indicator */}
          {!hasMore && trips.length > 0 && (
            <div className="end-of-trips" style={{ 
              textAlign: 'center', 
              padding: '20px',
              color: theme.text.secondary,
              fontSize: '16px'
            }}>
              تمام سفرها نمایش داده شد
            </div>
          )}
        </>
      )}

      {/* removed global ChatPopup for top-level support chat */}
    </div>
  );
};

export default TripsHistory;
